import React, { useState, useEffect, useRef, type KeyboardEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { View, Flex, Text, TextField, Button } from "./Common/spectrumCompat";
import { displayToasts } from "../Helpers/displayAppToast";
import FullScreenLoader from "./Common/FullScreenLoader";
import {
  getAccountId,
  getDXPRegistrationLink,
  persistPartnerLevel,
} from "../Helpers/utils";
import {
  showInvalidEmailMessage,
  setRegisteredCompanies,
  setPartnerRegistrationStatus,
  setImsProfileExists,
} from "../Redux/actions";
import { findPartnerEmail } from "../Services/PartnerRegistrationAPI";
import { PROGRAM_SPECIFIC_NAMES } from "../Helpers/constants";
import { prepareDxpInvitedContactWizardState } from "../Helpers/hydratePartnerRegistrationSession";
import enterEmailConfig from "../ContentConfig/EnterEmailStep";
import { DXP_EMAIL_PATTERN } from "../Helpers/dxpRegistrationValidation";
import "./css/EnterEmail.css";

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-enable @typescript-eslint/no-explicit-any */

const PROGRAM = "DXP";
const PROGRAM_TYPE = "dxp";

type EnterEmailProps = {
  previewMode?: boolean;
};

function EnterEmail({ previewMode = false }: EnterEmailProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registration = useSelector(
    (state: {
      registration?: Record<string, unknown>;
    }): Record<string, unknown> => state?.registration || {},
  );
  const emailMismatch = localStorage.getItem("emailMismatch");

  const [email, setEmail] = useState("");
  const emailTouchedRef = useRef(false);
  const emailDirtyRef = useRef(false);
  const emailRef = useRef(email);
  emailRef.current = email;

  const [validationState, setValidationState] = useState<
    "valid" | "invalid" | undefined
  >();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isInvalidEmailFromApi = registration?.isInvalidEmail ?? false;
  const autoSubmitMismatchEmailRef = useRef(false);

  const applyApiInvalidEmailError = () => {
    setValidationState("invalid");
    setErrorMessage(enterEmailConfig.errors.invalidEmail);
  };

  // Sync server-side invalid email error from Redux (e.g. on mount after redirect).
  useEffect(() => {
    if (isInvalidEmailFromApi) {
      applyApiInvalidEmailError();
    }
  }, [isInvalidEmailFromApi]);

  /** Live validation — only call when field is both touched and dirty. */
  const applyEmailFormatValidation = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValidationState("invalid");
      setErrorMessage(enterEmailConfig.errors.required);
      return;
    }
    if (!DXP_EMAIL_PATTERN.test(trimmed)) {
      setValidationState("invalid");
      setErrorMessage(enterEmailConfig.errors.pattern);
    } else {
      setValidationState("valid");
      setErrorMessage(undefined);
    }
  };

  const handleEmailFocus = () => {
    emailTouchedRef.current = true;
    // Edge: user edited before focus (e.g. autofill) — validate once focused + dirty.
    // Do not clear the API invalid-email error; Redux flag stays true until the user edits.
    if (emailDirtyRef.current && !isInvalidEmailFromApi) {
      applyEmailFormatValidation(emailRef.current);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    emailDirtyRef.current = true;
    if (isInvalidEmailFromApi) {
      dispatch(showInvalidEmailMessage(false));
    }
    if (!emailTouchedRef.current) {
      setValidationState(undefined);
      setErrorMessage(undefined);
      return;
    }
    applyEmailFormatValidation(value);
  };

  const isValidEmail = () => {
    if (!email.trim()) return false;
    return DXP_EMAIL_PATTERN.test(email.trim());
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const onSubmitSuccess = (response: any, stepNumber: number) => {
    const responseKeys = Object.keys(response);
    if (responseKeys.length > 0) {
      dispatch(setImsProfileExists(!!response?.imsProfileExists));
      if (response.isPublicDomain) {
        dispatch(showInvalidEmailMessage(true));
        applyApiInvalidEmailError();
        if (emailMismatch) {
          localStorage.removeItem("accountId");
          localStorage.removeItem("companyName");
          localStorage.removeItem("email");
          localStorage.removeItem("emailMismatch");
          localStorage.removeItem("partnerRegistrationFormStep");
          localStorage.removeItem("RedirectPath");
          localStorage.removeItem("selectedRegisterCompanyType");
          window.adobeIMS?.signOut?.({
            redirect_uri: getDXPRegistrationLink(),
          });
        }
      } else if (response?.status === "Pending") {
        navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.requestPending);
      } else if (
        response.code === "10003" ||
        (response?.accounts && !response?.code) ||
        (response.code === "10014" && response?.accounts?.length) ||
        (response.code === "10017" && !response?.accounts?.length)
      ) {
        const companies = response.accounts;
        const currentStep = stepNumber || 1;
        if (response.code === "10014" && response?.accId) {
          localStorage.setItem("accountId", response?.accId);
        }
        dispatch(setRegisteredCompanies(companies, currentStep));
        // Parent should render company selection when step=1 and companies exist
      } else if (response.code) {
        const accounts = response?.accounts || [];
        const accountId = getAccountId(accounts?.[0]);

        switch (response.code) {
          case "10001":
            localStorage.setItem("companyName", accounts?.[0]?.name);
            persistPartnerLevel(accounts?.[0]);
            dispatch(setPartnerRegistrationStatus("AlreadyRegistered", 0));
            navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.alreadyRegistered);
            break;

          case "10002":
            dispatch(
              setPartnerRegistrationStatus("RegisteredWithAnotherProgram", 0),
            );
            if (response?.programType) {
              localStorage.setItem(
                "alreadyRegisteredWith",
                response?.programType,
              );
            }
            navigate(
              PROGRAM_SPECIFIC_NAMES[PROGRAM].route
                .registeredWithAnotherProgram,
            );
            break;

          case "10004":
            navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.requestPending);
            break;

          case "10006":
            localStorage.removeItem("redirectPath");
            dispatch(showInvalidEmailMessage(true));
            applyApiInvalidEmailError();
            dispatch(setPartnerRegistrationStatus("NotRegistered", 0));
            if (emailMismatch) {
              localStorage.removeItem("accountId");
              localStorage.removeItem("companyName");
              localStorage.removeItem("email");
              localStorage.removeItem("emailMismatch");
              localStorage.removeItem("partnerRegistrationFormStep");
              localStorage.removeItem("RedirectPath");
              localStorage.removeItem("selectedRegisterCompanyType");
              window.adobeIMS?.signOut?.({
                redirect_uri: getDXPRegistrationLink(),
              });
            }
            break;

          case "10010":
            navigate(
              PROGRAM_SPECIFIC_NAMES[PROGRAM].route.sameDomainContactInactive,
            );
            break;

          case "10011":
            navigate(
              PROGRAM_SPECIFIC_NAMES[PROGRAM].route
                .differentDomainContactInactive,
            );
            break;

          case "10008":
          case "10009":
          case "10013":
            localStorage.setItem("companyName", accounts?.[0]?.name);
            persistPartnerLevel(accounts?.[0]);
            dispatch(setPartnerRegistrationStatus("AlreadyRegistered", 0));
            navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.alreadyRegistered);
            break;

          case "10014":
            dispatch(
              setPartnerRegistrationStatus("RegisteredWithAnotherProgram", 2),
            );
            if (response?.accId) {
              localStorage.setItem("accountId", response?.accId);
            }
            if (response?.programType) {
              localStorage.setItem(
                "alreadyRegisteredWith",
                response?.programType,
              );
            }
            navigate(
              PROGRAM_SPECIFIC_NAMES[PROGRAM].route
                .alreadyRegisteredWithAnotherProgram,
            );
            break;

          case "10018":
            if (accountId) {
              localStorage.setItem("accountId", accountId);
            }
            if (accounts?.[0]?.name) {
              localStorage.setItem("companyName", accounts?.[0]?.name);
            }
            if (response?.redirectTo) {
              localStorage.setItem("redirectTo", response.redirectTo);
            } else if (!localStorage.getItem("redirectTo")) {
              localStorage.setItem("redirectTo", "membershipdetails");
            }
            persistPartnerLevel(accounts?.[0]);
            dispatch(setPartnerRegistrationStatus("AlreadyRegistered", 0));
            navigate(
              PROGRAM_SPECIFIC_NAMES[PROGRAM].route.continueRegistration,
            );
            break;

          case "10019":
            if (accounts?.[0]?.id) {
              localStorage.setItem("accountId", accounts?.[0]?.id);
            }
            if (accounts?.[0]?.name) {
              localStorage.setItem("companyName", accounts?.[0]?.name);
            }
            persistPartnerLevel(accounts?.[0]);
            localStorage.removeItem("redirectTo");
            prepareDxpInvitedContactWizardState();
            navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.inviteContact);
            break;

          default:
            break;
        }
      }
    } else {
      displayToasts({
        type: "error",
        text: "Something went wrong Please Try Again Later.",
        autoClose: true,
      });
    }
  };

  const handleCheckEmail = (emailOverride?: string) => {
    const emailToCheck = (emailOverride ?? email).trim();
    if (!emailToCheck) {
      emailTouchedRef.current = true;
      emailDirtyRef.current = true;
      setValidationState("invalid");
      setErrorMessage(enterEmailConfig.errors.required);
      return;
    }
    if (!DXP_EMAIL_PATTERN.test(emailToCheck)) {
      emailTouchedRef.current = true;
      emailDirtyRef.current = true;
      setValidationState("invalid");
      setErrorMessage(enterEmailConfig.errors.pattern);
      return;
    }

    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — email check is disabled.",
        autoClose: true,
      });
      return;
    }

    setIsSubmitting(true);
    localStorage.removeItem("alreadyRegisteredWith");
    localStorage.removeItem("accountStatus");

    localStorage.setItem("email", emailToCheck);
    if (!emailOverride) {
      localStorage.setItem("searchedEmail", emailToCheck);
    }

    findPartnerEmail(emailToCheck, PROGRAM_TYPE)
      .then((data) => {
        if (data.status === 1) {
          if (data.response?.redirectTo) {
            localStorage.setItem("redirectTo", data.response?.redirectTo);
          }
          onSubmitSuccess(data.response, 1);
        } else if (data.status === 0) {
          displayToasts({
            type: "error",
            text: "Something went wrong Please Try Again Later.",
            autoClose: true,
          });
        }
      })
      .catch(() => {
        displayToasts({
          type: "error",
          text: "Something went wrong Please Try Again Later.",
          autoClose: true,
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleCheckEmailRef = useRef(handleCheckEmail);
  handleCheckEmailRef.current = handleCheckEmail;

  // After user chooses Continue on the mismatch page, re-run email check with their Adobe ID.
  useEffect(() => {
    if (previewMode || autoSubmitMismatchEmailRef.current) return;

    const mismatchAdobeEmail = localStorage.getItem("emailMismatch")?.trim();
    if (!mismatchAdobeEmail || !DXP_EMAIL_PATTERN.test(mismatchAdobeEmail)) {
      return;
    }

    autoSubmitMismatchEmailRef.current = true;
    setEmail(mismatchAdobeEmail);
    handleCheckEmailRef.current(mismatchAdobeEmail);
  }, [previewMode]);

  const handleEmailKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!isValidEmail() || isSubmitting) return;
    handleCheckEmail();
  };

  const submitEnabled = isValidEmail() && !isSubmitting;

  return (
    <>
      <View UNSAFE_className="dxp-wizard-main-column dxp-wizard-main-column--wide dxp-w-full">
        <Flex
          direction="column"
          gap={0}
          alignItems="center"
          UNSAFE_className="dxp-wizard-hero-text"
        >
          <Text UNSAFE_className="dxp-enter-email-hero-label">
            {enterEmailConfig.heroTitle}
          </Text>

          <div className="dxp-enter-email-hero-stack">
            <Text UNSAFE_className="dxp-enter-email-hero-line dxp-enter-email-hero-line--lead">
              {enterEmailConfig.heroThanks}
            </Text>
            <Text UNSAFE_className="dxp-enter-email-hero-line dxp-enter-email-hero-line--follow">
              {enterEmailConfig.heroGetStarted}
            </Text>
          </div>

          <View UNSAFE_className="dxp-wizard-step-panel dxp-enter-email-step-panel">
            <Text UNSAFE_className="dxp-enter-email-card-title">
              {enterEmailConfig.cardTitle}
            </Text>
            <div className="dxp-wizard-divider-purple" aria-hidden />
            <Text UNSAFE_className="dxp-enter-email-card-hint">
              {enterEmailConfig.cardHint}
            </Text>
            <TextField
              label={enterEmailConfig.fieldLabel}
              labelAlign="start"
              isRequired
              necessityIndicator="icon"
              placeholder="Enter your email"
              value={email}
              onFocus={handleEmailFocus}
              onChange={handleEmailChange}
              onKeyDown={handleEmailKeyDown}
              isInvalid={validationState === "invalid"}
              errorMessage={errorMessage}
              maxLength={100}
              isDisabled={isSubmitting}
              UNSAFE_className="enterEmail_cmpnyEmailId dxp-enter-email-field dxp-w-full"
            />
            <Flex
              direction="row"
              justifyContent="end"
              UNSAFE_className="dxp-enter-email-actions-row"
            >
              <Button
                variant="accent"
                isDisabled={!submitEnabled}
                onPress={() => handleCheckEmail()}
                UNSAFE_className={[
                  "dxp-enter-email-submit",
                  submitEnabled
                    ? "dxp-enter-email-submit--active"
                    : "dxp-enter-email-submit--inactive",
                ].join(" ")}
              >
                {enterEmailConfig.buttonLabel}
              </Button>
            </Flex>
          </View>
        </Flex>
      </View>
      {isSubmitting && <FullScreenLoader />}
    </>
  );
}

EnterEmail.defaultProps = {
  previewMode: false,
};

export default EnterEmail;
